import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import {
  DatePicker, Form, Input, Select, Spin, Upload, message,
} from 'antd';
import { Download, FileDown, Paperclip, Plus, Settings, Trash2, Upload as UploadIcon } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  opportunityApi, briefCycleApi, brandApi, enumApi, permissionConfigApi, planVersionApi,
} from '../api/services';
import type {
  OpportunitySaveDTO,
  OpportunityVO,
} from '../api/ipc/opportunity';
import { resolveAttachmentDownloadUrl } from '../api/ipc/opportunity';
import {
  IpcButton,
  IpcDateRangePicker,
  IpcFilterToolbar,
  IpcFormField,
  IpcGradeTag,
  IpcModal,
  IpcRowActions,
  IpcStatusBadge,
  IpcTable,
  ipcConfirm,
  type IpcFilterChipItem,
} from '../components/ui';
import { OpportunityAttachmentCell } from '../components/OpportunityAttachmentCell';
import { PlanningLinkStatusHover } from '../components/PlanningLinkStatusHover';
import { hasSystemAdminPermission } from '../utils/permissionCodes';
import { styleLabelToBadgeVariant } from '../utils/ipcStatusVisual';
import './OpportunityPlanning.css';
import {
  DEFAULT_CYCLE,
  normalizeCycleConfig,
  type CycleConfig,
} from './BriefCycleRuleEditor';


const DEFAULT_STATUS = '全部';

type EditAttachmentItem = {
  key: string;
  fileId?: number;
  fileName: string;
  /** 上传接口返回的相对路径（入库用） */
  fileUrl: string;
  downloadUrl?: string;
  fileSize?: number;
  contentType?: string;
};

function OpportunityStatusCell({
  row,
  statusStyleMap,
}: {
  row: OpportunityVO;
  statusStyleMap: Record<string, string>;
}) {
  const badge = (
    <IpcStatusBadge variant={styleLabelToBadgeVariant(statusStyleMap[row.status])}>
      {row.status}
    </IpcStatusBadge>
  );

  return (
    <PlanningLinkStatusHover
      status={row.status}
      linkedIpds={row.linkedIpds}
      linkedIpcs={row.linkedIpcs}
    >
      {badge}
    </PlanningLinkStatusHover>
  );
}

type OppFilters = {
  keyword: string;
  brand?: string;
  status: string;
  grade?: string;
  productType?: string;
  inboundDateFrom?: string;
  inboundDateTo?: string;
};

function emptyOppFilters(): OppFilters {
  return {
    keyword: '',
    brand: undefined,
    status: DEFAULT_STATUS,
    grade: undefined,
    productType: undefined,
    inboundDateFrom: undefined,
    inboundDateTo: undefined,
  };
}

const OpportunityPool: FC = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<OpportunityVO[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 筛选栏草稿（与项目排期一致：点「查询」后写入已应用并出 chips）
  const [filterKeyword, setFilterKeyword] = useState('');
  const [filterBrand, setFilterBrand] = useState<string>();
  const [filterStatus, setFilterStatus] = useState(DEFAULT_STATUS);
  const [filterGrade, setFilterGrade] = useState<string>();
  const [filterProductType, setFilterProductType] = useState<string>();
  const [filterInboundRange, setFilterInboundRange] = useState<[string | undefined, string | undefined]>([undefined, undefined]);
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [appliedBrand, setAppliedBrand] = useState<string>();
  const [appliedStatus, setAppliedStatus] = useState(DEFAULT_STATUS);
  const [appliedGrade, setAppliedGrade] = useState<string>();
  const [appliedProductType, setAppliedProductType] = useState<string>();
  const [appliedInboundFrom, setAppliedInboundFrom] = useState<string>();
  const [appliedInboundTo, setAppliedInboundTo] = useState<string>();

  const [brands, setBrands] = useState<{ label: string; value: string }[]>([]);
  const [grades, setGrades] = useState<string[]>(['S', 'A', 'B', 'C', 'D', '待定']);
  const [molds, setMolds] = useState<string[]>(['开模', '公模', '待定']);
  const [regs, setRegs] = useState<string[]>(['否', '是']);
  const [sources, setSources] = useState<string[]>(['日常', '规划']);
  const [statuses, setStatuses] = useState<string[]>(['待立项', '进行中', '已完成', '已终止']);
  const [statusStyleMap, setStatusStyleMap] = useState<Record<string, string>>({
    待立项: 'default',
    进行中: 'processing',
    已完成: 'success',
    已终止: 'error',
  });
  const [productTypes, setProductTypes] = useState<string[]>(['新品', '升级', '礼盒', 'GWP', '规格衍生']);
  const [permCodes, setPermCodes] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<OpportunityVO | null>(null);
  const [form] = Form.useForm();
  /** 每次打开弹窗递增，强制 Form/Modal 子树重挂载，避免 Form preserve 缓存上次值 */
  const [formMountKey, setFormMountKey] = useState(0);
  const [cycleOpen, setCycleOpen] = useState(false);
  const [cycleCfg, setCycleCfg] = useState<CycleConfig>(DEFAULT_CYCLE);
  const [cycleTrial, setCycleTrial] = useState<{
    brand?: string;
    category?: string;
    productType?: string;
    grade?: string;
    moldType?: string;
    needCert?: string;
    sourceType?: string;
    inbound?: dayjs.Dayjs | null;
  }>({
    needCert: '否',
    moldType: '开模',
    grade: 'S',
    sourceType: '日常',
    productType: '新品',
    inbound: dayjs().add(12, 'month'),
  });
  const [cycleTrialResult, setCycleTrialResult] = useState<string>('');
  const [bringOpen, setBringOpen] = useState(false);
  const [plans, setPlans] = useState<{ id: number; label: string }[]>([]);
  const [bringPlanId, setBringPlanId] = useState<number>();
  const [editAttachments, setEditAttachments] = useState<EditAttachmentItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const attachKeyRef = useRef(0);
  /** create=新增 / edit=编辑 / view=详情（只读，复用同一弹框） */
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'view'>('create');

  const isAdmin = hasSystemAdminPermission(permCodes);

  const loadEnums = useCallback(async () => {
    const [b, g, m, r, s, st, pt, codes] = await Promise.all([
      brandApi.getList().catch(() => []),
      enumApi.getList('IPC_OPP_GRADE').catch(() => ({ list: [] })),
      enumApi.getList('IPC_OPP_MOLD').catch(() => ({ list: [] })),
      enumApi.getList('IPC_OPP_REG').catch(() => ({ list: [] })),
      enumApi.getList('IPC_OPP_SOURCE').catch(() => ({ list: [] })),
      enumApi.getList('IPC_OPP_STATUS').catch(() => ({ list: [] })),
      enumApi.getList('IPC_OPP_PTYPE').catch(() => ({ list: [] })),
      permissionConfigApi.getMyPermissionCodes().catch(() => [] as string[]),
    ]);
    setBrands((b || []).map((x: any) => ({
      label: x.name || x.code,
      value: x.name || x.code,
    })).filter((x: any) => x.value));
    if (g.list?.length) setGrades(g.list.map(i => i.value));
    if (m.list?.length) setMolds(m.list.map(i => i.value));
    if (r.list?.length) setRegs(r.list.map(i => i.value));
    if (s.list?.length) setSources(s.list.map(i => i.value));
    if (st.list?.length) {
      setStatuses(st.list.map(i => i.value));
      const styleMap: Record<string, string> = {};
      st.list.forEach(i => {
        if (i.value) styleMap[i.value] = i.style_label || '';
      });
      setStatusStyleMap(prev => ({ ...prev, ...styleMap }));
    }
    if (pt.list?.length) setProductTypes(pt.list.map(i => i.value));
    setPermCodes(codes || []);
  }, []);

  const load = useCallback(async (
    page = pageNum,
    size = pageSize,
    filters?: OppFilters,
  ) => {
    const f = filters ?? {
      keyword: appliedKeyword,
      brand: appliedBrand,
      status: appliedStatus,
      grade: appliedGrade,
      productType: appliedProductType,
      inboundDateFrom: appliedInboundFrom,
      inboundDateTo: appliedInboundTo,
    };
    setLoading(true);
    try {
      const res = await opportunityApi.getList({
        keyword: f.keyword,
        brand: f.brand,
        status: f.status,
        grade: f.grade,
        productType: f.productType,
        inboundDateFrom: f.inboundDateFrom,
        inboundDateTo: f.inboundDateTo,
        pageNum: page,
        pageSize: size,
      });
      setRows(res.list || []);
      setTotal(res.total || 0);
      setPageNum(page);
      setPageSize(size);
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [
    appliedKeyword, appliedBrand, appliedStatus, appliedGrade,
    appliedProductType, appliedInboundFrom, appliedInboundTo,
    pageNum, pageSize,
  ]);

  useEffect(() => { void loadEnums(); }, [loadEnums]);
  useEffect(() => { void load(1, pageSize); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFiltersAndLoad = (
    next: OppFilters,
    page = 1,
    size = pageSize,
  ) => {
    setFilterKeyword(next.keyword);
    setFilterBrand(next.brand);
    setFilterStatus(next.status);
    setFilterGrade(next.grade);
    setFilterProductType(next.productType);
    setFilterInboundRange([next.inboundDateFrom, next.inboundDateTo]);
    setAppliedKeyword(next.keyword);
    setAppliedBrand(next.brand);
    setAppliedStatus(next.status);
    setAppliedGrade(next.grade);
    setAppliedProductType(next.productType);
    setAppliedInboundFrom(next.inboundDateFrom);
    setAppliedInboundTo(next.inboundDateTo);
    void load(page, size, next);
  };

  const submitFilters = () => {
    applyFiltersAndLoad({
      keyword: filterKeyword,
      brand: filterBrand,
      status: filterStatus,
      grade: filterGrade,
      productType: filterProductType,
      inboundDateFrom: filterInboundRange[0] || undefined,
      inboundDateTo: filterInboundRange[1] || undefined,
    });
  };

  /** 重置：恢复默认「全部」 */
  const resetFiltersToDefault = () => {
    applyFiltersAndLoad(emptyOppFilters());
  };

  /** 清除全部：与重置相同（默认即空条件） */
  const clearAllFilters = () => {
    resetFiltersToDefault();
  };

  // 已应用筛选 chips：与项目排期相同文案「字段: 值」，含默认状态
  const appliedChips: IpcFilterChipItem[] = useMemo(() => {
    const chips: IpcFilterChipItem[] = [];
    if (appliedKeyword.trim()) {
      chips.push({ key: 'keyword', label: `机会名称: ${appliedKeyword.trim()}` });
    }
    if (appliedBrand) {
      chips.push({ key: 'brand', label: `品牌: ${appliedBrand}` });
    }
    if (appliedStatus && appliedStatus !== '全部') {
      chips.push({ key: 'status', label: `状态: ${appliedStatus}` });
    }
    if (appliedGrade) {
      chips.push({ key: 'grade', label: `定级: ${appliedGrade}` });
    }
    if (appliedProductType) {
      chips.push({ key: 'productType', label: `产品类型: ${appliedProductType}` });
    }
    if (appliedInboundFrom || appliedInboundTo) {
      chips.push({
        key: 'inbound',
        label: `目标入库时间: ${appliedInboundFrom || '…'} ~ ${appliedInboundTo || '…'}`,
      });
    }
    return chips;
  }, [
    appliedKeyword, appliedBrand, appliedStatus, appliedGrade,
    appliedProductType, appliedInboundFrom, appliedInboundTo,
  ]);

  const removeChip = (key: string) => {
    const next: OppFilters = {
      keyword: appliedKeyword,
      brand: appliedBrand,
      status: appliedStatus,
      grade: appliedGrade,
      productType: appliedProductType,
      inboundDateFrom: appliedInboundFrom,
      inboundDateTo: appliedInboundTo,
    };
    if (key === 'keyword') next.keyword = '';
    if (key === 'brand') next.brand = undefined;
    if (key === 'status') next.status = '全部';
    if (key === 'grade') next.grade = undefined;
    if (key === 'productType') next.productType = undefined;
    if (key === 'inbound') {
      next.inboundDateFrom = undefined;
      next.inboundDateTo = undefined;
    }
    applyFiltersAndLoad(next);
  };

  const [exportingDetail, setExportingDetail] = useState(false);
  const exportDetailExcel = async () => {
    if (!editing?.id) {
      message.warning('暂无可导出内容');
      return;
    }
    setExportingDetail(true);
    try {
      await opportunityApi.exportDetail(editing.id);
      message.success('导出成功');
    } catch (e: any) {
      message.error(e?.message || '导出失败');
    } finally {
      setExportingDetail(false);
    }
  };

  const [exportingList, setExportingList] = useState(false);
  const exportListExcel = async () => {
    setExportingList(true);
    try {
      await opportunityApi.exportList({
        keyword: appliedKeyword || undefined,
        brand: appliedBrand,
        status: appliedStatus,
        grade: appliedGrade,
        productType: appliedProductType,
        inboundDateFrom: appliedInboundFrom,
        inboundDateTo: appliedInboundTo,
      });
      message.success('导出成功');
    } catch (e: any) {
      message.error(e?.message || '导出失败');
    } finally {
      setExportingList(false);
    }
  };

  /** 新增：全部字段空，不预填任何默认值（含必填） */
  const createFormDefaults = () => ({
    name: undefined,
    brand: undefined,
    productLine: undefined,
    category: undefined,
    productType: undefined,
    plannedSkuCount: undefined,
    grade: undefined,
    estimatedInboundDate: null,
    moldType: undefined,
    needCert: undefined,
    sourceType: undefined,
    remark: undefined,
  });

  const openForm = (row?: OpportunityVO, mode: 'create' | 'edit' | 'view' = row ? 'edit' : 'create') => {
    savingRef.current = false;
    setSaving(false);
    setFormMode(mode);
    setEditing(row || null);
    setEditAttachments((row?.attachments || []).map((a, idx) => ({
      key: `att-${a.id || a.fileId || idx}`,
      fileId: a.fileId,
      fileName: a.fileName,
      fileUrl: a.fileUrl || a.downloadUrl || '',
      downloadUrl: a.downloadUrl || a.fileUrl,
      fileSize: a.fileSize,
      contentType: a.contentType,
    })));
    // 每次打开换 key：整页重挂载，不沿用 FormStore 缓存
    setFormMountKey(k => k + 1);
    form.resetFields();
    form.setFieldsValue(row ? {
      name: row.name,
      brand: row.brand,
      productLine: row.productLine,
      category: row.category,
      productType: row.productType,
      plannedSkuCount: row.plannedSkuCount,
      grade: row.grade,
      estimatedInboundDate: row.estimatedInboundDate ? dayjs(row.estimatedInboundDate) : null,
      moldType: row.moldType,
      needCert: row.needCert,
      sourceType: row.sourceType,
      remark: row.remark,
    } : createFormDefaults());
    setEditOpen(true);
  };

  const openDetail = (row: OpportunityVO) => openForm(row, 'view');
  const openEdit = (row?: OpportunityVO) => openForm(row, row ? 'edit' : 'create');
  const closeForm = () => {
    setEditOpen(false);
    form.resetFields();
    setEditAttachments([]);
    setEditing(null);
  };
  const isViewMode = formMode === 'view';

  const onUploadFiles = async (files: File[]) => {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const uploaded = await opportunityApi.uploadToFileSys(file);
        attachKeyRef.current += 1;
        setEditAttachments(prev => [...prev, {
          key: `new-${attachKeyRef.current}`,
          fileId: uploaded.fileId,
          fileName: uploaded.fileName,
          fileUrl: uploaded.fileUrl,
          downloadUrl: resolveAttachmentDownloadUrl(uploaded.fileUrl),
          fileSize: uploaded.fileSize,
          contentType: uploaded.contentType,
        }]);
      }
    } catch (e: any) {
      message.error(e?.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const removeEditAttachment = (item: EditAttachmentItem) => {
    setEditAttachments(prev => prev.filter(x => x.key !== item.key));
  };

  const save = async () => {
    if (savingRef.current) return;
    const v = await form.validateFields();
    const payload: OpportunitySaveDTO = {
      id: editing?.id,
      name: v.name,
      brand: v.brand,
      productLine: v.productLine,
      category: v.category,
      productType: v.productType,
      grade: v.grade,
      plannedSkuCount: (v.plannedSkuCount ?? '').trim(),
      estimatedInboundDate: v.estimatedInboundDate ? v.estimatedInboundDate.format('YYYY-MM-DD') : null,
      moldType: v.moldType,
      needCert: v.needCert,
      sourceType: v.sourceType,
      remark: v.remark,
      attachments: editAttachments
        .filter(x => !!x.fileUrl)
        .map(x => ({
          fileId: x.fileId || 0,
          fileName: x.fileName,
          fileUrl: x.fileUrl,
          fileSize: x.fileSize || 0,
          contentType: x.contentType || '',
        })),
    };
    savingRef.current = true;
    setSaving(true);
    try {
      if (editing?.id) {
        await opportunityApi.update(payload);
      } else {
        await opportunityApi.create(payload);
      }
      message.success('已保存');
      closeForm();
      void load();
    } catch (e: any) {
      message.error(e?.message || '保存失败');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const onDelete = (id: number) => {
    ipcConfirm({
      title: '确认删除该机会？',
      onOk: async () => {
        try {
          await opportunityApi.delete(id);
          message.success('已删除');
          void load();
        } catch (e: any) {
          message.error(e?.message || '删除失败');
        }
      },
    });
  };

  const openCycle = async () => {
    if (!isAdmin) {
      message.warning('仅系统管理员可设置排期周期');
      return;
    }
    try {
      const cfg = await briefCycleApi.get();
      let parsed: any = DEFAULT_CYCLE;
      try {
        parsed = JSON.parse(cfg.configJson || '{}');
      } catch {
        parsed = DEFAULT_CYCLE;
      }
      setCycleCfg(normalizeCycleConfig(parsed));
      setCycleTrialResult('');
      setCycleOpen(true);
    } catch (e: any) {
      message.error(e?.message || '加载配置失败');
    }
  };

  const runCycleTrial = async () => {
    try {
      const res = await briefCycleApi.trial({
        estimatedInboundDate: cycleTrial.inbound ? cycleTrial.inbound.format('YYYY-MM-DD') : null,
        brand: cycleTrial.brand,
        category: cycleTrial.category,
        productType: cycleTrial.productType,
        grade: cycleTrial.grade,
        moldType: cycleTrial.moldType,
        needCert: cycleTrial.needCert,
        sourceType: cycleTrial.sourceType,
        configJson: JSON.stringify({ version: 5, script: cycleCfg.script }),
      });
      if ((res as any).error) {
        setCycleTrialResult(`脚本错误：${(res as any).error}`);
        return;
      }
      const brief = res.suggestedBriefDate
        ? String(res.suggestedBriefDate).slice(0, 10)
        : '—';
      setCycleTrialResult(
        `命中「${res.label || '—'}」· ${res.months} 个月 · 建议 Brief：${brief}`,
      );
    } catch (e: any) {
      message.error(e?.message || '试算失败');
    }
  };

  const saveCycle = async () => {
    if (!cycleCfg.script?.trim()) {
      message.warning('请填写 JS 脚本');
      return;
    }
    try {
      const payload: CycleConfig = { version: 5, script: cycleCfg.script };
      await briefCycleApi.save(JSON.stringify(payload));
      message.success('配置已保存');
      setCycleOpen(false);
    } catch (e: any) {
      message.error(e?.message || '保存失败');
    }
  };

  const openBring = async () => {
    if (!selectedIds.length) {
      message.warning('请先勾选机会');
      return;
    }
    try {
      const list = await planVersionApi.getList();
      const drafting = (list || []).filter(p => p.state === '编制中');
      setPlans(drafting.map(p => ({ id: p.id, label: `${p.brand} · ${p.planYear} 版` })));
      setBringPlanId(drafting[0]?.id);
      setBringOpen(true);
    } catch (e: any) {
      message.error(e?.message || '加载规划失败');
    }
  };

  const doBring = async () => {
    if (!bringPlanId) {
      message.warning('请选择编制中的三年规划');
      return;
    }
    try {
      await planVersionApi.bring(bringPlanId, selectedIds);
      message.success('已带入三年规划');
      setBringOpen(false);
      setSelectedIds([]);
    } catch (e: any) {
      message.error(e?.message || '带入失败');
    }
  };

  const columns: ColumnsType<OpportunityVO> = useMemo(() => [
    { title: '机会名称', dataIndex: 'name', ellipsis: true },
    { title: '品牌', dataIndex: 'brand', width: 120, ellipsis: true },
    { title: '品线', dataIndex: 'productLine', width: 110, ellipsis: true },
    { title: '品类', dataIndex: 'category', width: 120, ellipsis: true },
    { title: '产品类型', dataIndex: 'productType', width: 110, ellipsis: true },
    { title: '定级', dataIndex: 'grade', width: 72, render: (v: string) => <IpcGradeTag grade={v} /> },
    {
      title: '计划 SKU 数',
      dataIndex: 'plannedSkuCount',
      width: 110,
      ellipsis: true,
      render: (v?: string) => v || '—',
    },
    {
      title: '目标入库时间', dataIndex: 'estimatedInboundDate', width: 120,
      render: (v) => (v ? String(v).slice(0, 10) : '—'),
    },
    {
      title: '建议 Brief', dataIndex: 'suggestedBriefDate', width: 120,
      render: (v) => (v ? String(v).slice(0, 10) : '—'),
    },
    { title: '开模', dataIndex: 'moldType', width: 80 },
    { title: '特证', dataIndex: 'needCert', width: 72 },
    { title: '来源', dataIndex: 'sourceType', width: 80 },
    {
      title: '附件',
      dataIndex: 'attachmentCount',
      width: 72,
      render: (_: number, row) => <OpportunityAttachmentCell row={row} />,
    },
    {
      title: '状态', dataIndex: 'status', width: 100,
      render: (_: string, row) => (
        <OpportunityStatusCell row={row} statusStyleMap={statusStyleMap} />
      ),
    },
    {
      title: '操作', width: 180, fixed: 'right',
      render: (_, row) => (
        <IpcRowActions
          actions={[
            { key: 'detail', label: '详情', onClick: () => openDetail(row) },
            { key: 'edit', label: '编辑', onClick: () => openEdit(row) },
            ...(isAdmin
              ? [{ key: 'delete', label: '删除', danger: true, onClick: () => onDelete(row.id) }]
              : []),
          ]}
        />
      ),
    },
  ], [isAdmin, statusStyleMap]);

  return (
    <div className="ipc-page ipc-page-stack opportunity-planning-page">
      <IpcFilterToolbar
        onSubmit={submitFilters}
        onReset={resetFiltersToDefault}
        chipItems={appliedChips}
        onRemoveChip={removeChip}
        onClearChips={clearAllFilters}
        extra={(
          <>
            {selectedIds.length > 0 && (
              <IpcButton variant="secondary" onClick={() => void openBring()}>
                带入三年规划（{selectedIds.length}）
              </IpcButton>
            )}
            {isAdmin && (
              <IpcButton variant="secondary" icon={<Settings size={14} />} onClick={() => void openCycle()}>
                排期周期设置
              </IpcButton>
            )}
            <IpcButton
              variant="secondary"
              icon={<Download size={14} />}
              loading={exportingList}
              onClick={() => void exportListExcel()}
            >
              导出
            </IpcButton>
            <IpcButton variant="primary" icon={<Plus size={14} />} onClick={() => openEdit()}>
              新增机会
            </IpcButton>
          </>
        )}
      >
        <Input
          placeholder="机会名称"
          allowClear
          value={filterKeyword}
          onChange={e => setFilterKeyword(e.target.value)}
          onPressEnter={submitFilters}
          className="ipc-filter-bar__field"
        />
        <Select
          allowClear
          placeholder="全部品牌"
          value={filterBrand}
          options={brands}
          onChange={v => setFilterBrand(v)}
          className="ipc-filter-bar__field"
          showSearch
          optionFilterProp="label"
          getPopupContainer={() => document.body}
        />
        <Select
          placeholder="状态"
          value={filterStatus}
          options={[{ value: '全部', label: '全部' }, ...statuses.map(s => ({ value: s, label: s }))]}
          onChange={v => setFilterStatus(v)}
          className="ipc-filter-bar__field"
          getPopupContainer={() => document.body}
        />
        <Select
          allowClear
          placeholder="定级"
          value={filterGrade}
          options={grades.map(g => ({ value: g, label: g }))}
          onChange={v => setFilterGrade(v)}
          className="ipc-filter-bar__field"
          getPopupContainer={() => document.body}
        />
        <Select
          allowClear
          placeholder="产品类型"
          value={filterProductType}
          options={productTypes.map(g => ({ value: g, label: g }))}
          onChange={v => setFilterProductType(v)}
          className="ipc-filter-bar__field"
          getPopupContainer={() => document.body}
        />
        <IpcDateRangePicker
          variant="filter"
          value={filterInboundRange}
          onChange={([start, end]) => setFilterInboundRange([start, end])}
          className="ipc-filter-bar__field ipc-filter-bar__field--range"
          placeholder={['目标入库时间起', '目标入库时间止']}
        />
      </IpcFilterToolbar>

      <IpcTable<OpportunityVO>
        compact
        frameless
        resizable
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        tableLayout="fixed"
        className="opportunity-planning-table"
        scroll={{ x: 'max-content' }}
        autoHeight
        autoHeightDeps={[rows.length, pageNum, pageSize, loading]}
        panelClassName="opportunity-table-panel"
        scrollClassName={[
          'opportunity-table-scroll',
          rows.length === 0 && !loading ? 'ipc-empty-host' : '',
        ].filter(Boolean).join(' ')}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: keys => setSelectedIds(keys as number[]),
          getCheckboxProps: r => ({ disabled: r.status !== '待立项' && r.status !== '进行中' }),
        }}
        pagination={total > 0 ? {
          className: 'ipc-table-pagination',
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => { void load(p, ps); },
        } : false}
        emptyTitle="暂无机会"
        emptyDescription="请调整筛选条件或新增机会"
      />

      <IpcModal
        key={`opp-form-modal-${formMountKey}`}
        className={['opp-form-modal', isViewMode ? 'opp-detail-modal' : ''].filter(Boolean).join(' ')}
        title={isViewMode ? (
          <div className="opp-detail-modal__title-bar">
            <span className="opp-detail-modal__title-text">
              {editing ? `机会详情 · ${editing.name}` : '机会详情'}
            </span>
            <button
              type="button"
              className="opp-detail-modal__export"
              title="导出 Excel"
              aria-label="导出 Excel"
              disabled={exportingDetail}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                void exportDetailExcel();
              }}
            >
              <FileDown size={16} />
            </button>
          </div>
        ) : (formMode === 'edit' ? '编辑机会' : '新增机会')}
        open={editOpen}
        onCancel={closeForm}
        onOk={isViewMode ? undefined : () => void save()}
        width={640}
        destroyOnHidden
        okText="保存"
        confirmLoading={saving}
        cancelDisabled={saving}
        footer={isViewMode ? (
          <div className="ipc-modal__footer-actions">
            <IpcButton variant="primary" onClick={closeForm}>关闭</IpcButton>
          </div>
        ) : undefined}
      >
        <Form
          key={`opp-form-${formMountKey}`}
          form={form}
          layout="vertical"
          className="opportunity-edit-form"
          requiredMark={false}
          disabled={isViewMode}
          preserve={false}
          initialValues={formMode === 'create' ? createFormDefaults() : undefined}
        >
          <div className="opportunity-edit-form__grid">
            <IpcFormField label="机会名称" required={!isViewMode} fullWidth>
              <Form.Item name="name" rules={isViewMode ? undefined : [{ required: true, message: '请填写机会名称' }]} noStyle>
                <Input placeholder="产品名 / 想法，例如：油蜜卸妆油 周年庆新香" />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="品牌" required={!isViewMode}>
              <Form.Item name="brand" rules={isViewMode ? undefined : [{ required: true, message: '请选择品牌' }]} noStyle>
                <Select
                  options={brands}
                  showSearch
                  optionFilterProp="label"
                  placeholder="请选择"
                  getPopupContainer={() => document.body}
                />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="品线" required={!isViewMode}>
              <Form.Item name="productLine" rules={isViewMode ? undefined : [{ required: true, message: '请填写品线' }]} noStyle>
                <Input placeholder="例如：面部" />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="品类" required={!isViewMode}>
              <Form.Item name="category" rules={isViewMode ? undefined : [{ required: true, message: '请填写品类' }]} noStyle>
                <Input placeholder="例如：面部-清洁" />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="产品类型" required={!isViewMode}>
              <Form.Item name="productType" rules={isViewMode ? undefined : [{ required: true, message: '请选择产品类型' }]} noStyle>
                <Select
                  options={productTypes.map(g => ({ value: g, label: g }))}
                  placeholder="请选择"
                  getPopupContainer={() => document.body}
                />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="计划 SKU 数" fullWidth>
              <Form.Item name="plannedSkuCount" noStyle>
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder="文本，选填" />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="定级" required={!isViewMode}>
              <Form.Item name="grade" rules={isViewMode ? undefined : [{ required: true, message: '请选择定级' }]} noStyle>
                <Select
                  options={grades.map(g => ({ value: g, label: g }))}
                  placeholder="请选择"
                  getPopupContainer={() => document.body}
                />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="目标入库时间">
              <Form.Item name="estimatedInboundDate" noStyle>
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="选填"
                  getPopupContainer={() => document.body}
                />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="开模" required={!isViewMode}>
              <Form.Item name="moldType" rules={isViewMode ? undefined : [{ required: true, message: '请选择开模' }]} noStyle>
                <Select
                  options={molds.map(g => ({ value: g, label: g }))}
                  placeholder="请选择"
                  getPopupContainer={() => document.body}
                />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="特证" required={!isViewMode}>
              <Form.Item name="needCert" rules={isViewMode ? undefined : [{ required: true, message: '请选择特证' }]} noStyle>
                <Select
                  options={regs.map(g => ({ value: g, label: g }))}
                  placeholder="请选择"
                  getPopupContainer={() => document.body}
                />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="来源类型" required={!isViewMode}>
              <Form.Item name="sourceType" rules={isViewMode ? undefined : [{ required: true, message: '请选择来源类型' }]} noStyle>
                <Select
                  options={sources.map(g => ({ value: g, label: g }))}
                  placeholder="请选择"
                  getPopupContainer={() => document.body}
                />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="备注" fullWidth>
              <Form.Item name="remark" noStyle>
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  placeholder="选填：定位补充、资源约束、关联背景等（不强制填写 Brief）"
                />
              </Form.Item>
            </IpcFormField>
            <IpcFormField label="附件" fullWidth>
              <div className="opp-attachment-editor">
                {!isViewMode && (
                  <Upload
                    multiple
                    showUploadList={false}
                    beforeUpload={(file, fileList) => {
                      // antd 会对每个文件调用；仅在首个触发批量上传
                      if (fileList[0] === file) {
                        void onUploadFiles(fileList as unknown as File[]);
                      }
                      return false;
                    }}
                  >
                    <IpcButton
                      variant="secondary"
                      icon={uploading ? undefined : <UploadIcon size={14} />}
                      disabled={uploading}
                    >
                      {uploading ? <Spin size="small" /> : null}
                      {uploading ? '上传中…' : '上传附件'}
                    </IpcButton>
                  </Upload>
                )}
                {editAttachments.length > 0 ? (
                  <ul className="opp-attachment-editor__list">
                    {editAttachments.map(item => {
                      const href = resolveAttachmentDownloadUrl(item.downloadUrl || item.fileUrl);
                      return (
                        <li key={item.key} className="opp-attachment-editor__item">
                          <Paperclip size={14} className="opp-attachment-editor__icon" />
                          {href ? (
                            <a
                              className="opp-attachment-editor__name"
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={item.fileName}
                            >
                              {item.fileName}
                            </a>
                          ) : (
                            <span className="opp-attachment-editor__name" title={item.fileName}>
                              {item.fileName}
                            </span>
                          )}
                          {!isViewMode && (
                            <button
                              type="button"
                              className="opp-attachment-editor__remove"
                              onClick={() => removeEditAttachment(item)}
                              aria-label="删除附件"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  isViewMode ? <span className="opp-attachment-editor__empty">—</span> : null
                )}
              </div>
            </IpcFormField>
          </div>
        </Form>
      </IpcModal>

      <IpcModal
        title="排期周期设置"
        open={cycleOpen}
        onCancel={() => setCycleOpen(false)}
        onOk={() => void saveCycle()}
        width={880}
        okText="保存"
        className="ipc-cycle-modal"
      >
        <div className="ipc-cycle-body">
          <div className="ipc-cycle-main">
            <div className="ipc-cycle-script-wrap">
              <Input.TextArea
                className="ipc-cycle-script"
                value={cycleCfg.script}
                onChange={e => setCycleCfg({ version: 5, script: e.target.value })}
                spellCheck={false}
              />
            </div>

            <div className="ipc-cycle-trial">
              <div className="ipc-cycle-trial-title">试算</div>
              <div className="ipc-cycle-trial-fields">
                <Select
                  size="small"
                  placeholder="品牌"
                  allowClear
                  showSearch
                  value={cycleTrial.brand}
                  options={brands}
                  onChange={v => setCycleTrial(t => ({ ...t, brand: v }))}
                  getPopupContainer={() => document.body}
                />
                <Input
                  size="small"
                  placeholder="品类"
                  value={cycleTrial.category}
                  onChange={e => setCycleTrial(t => ({ ...t, category: e.target.value }))}
                />
                <Select
                  size="small"
                  placeholder="产品类型"
                  allowClear
                  value={cycleTrial.productType}
                  options={productTypes.map(v => ({ value: v, label: v }))}
                  onChange={v => setCycleTrial(t => ({ ...t, productType: v }))}
                  getPopupContainer={() => document.body}
                />
                <Select
                  size="small"
                  placeholder="定级"
                  value={cycleTrial.grade}
                  options={grades.map(v => ({ value: v, label: v }))}
                  onChange={v => setCycleTrial(t => ({ ...t, grade: v }))}
                  getPopupContainer={() => document.body}
                />
                <Select
                  size="small"
                  placeholder="开模"
                  value={cycleTrial.moldType}
                  options={molds.map(v => ({ value: v, label: v }))}
                  onChange={v => setCycleTrial(t => ({ ...t, moldType: v }))}
                  getPopupContainer={() => document.body}
                />
                <Select
                  size="small"
                  placeholder="特证"
                  value={cycleTrial.needCert}
                  options={regs.map(v => ({ value: v, label: v }))}
                  onChange={v => setCycleTrial(t => ({ ...t, needCert: v }))}
                  getPopupContainer={() => document.body}
                />
                <Select
                  size="small"
                  placeholder="来源"
                  value={cycleTrial.sourceType}
                  options={sources.map(v => ({ value: v, label: v }))}
                  onChange={v => setCycleTrial(t => ({ ...t, sourceType: v }))}
                  getPopupContainer={() => document.body}
                />
                <DatePicker
                  size="small"
                  value={cycleTrial.inbound}
                  onChange={v => setCycleTrial(t => ({ ...t, inbound: v }))}
                  placeholder="目标入库时间"
                  style={{ width: '100%' }}
                />
                <IpcButton size="small" block onClick={() => void runCycleTrial()}>试算</IpcButton>
              </div>
              {cycleTrialResult && (
                <div className="ipc-cycle-trial-result">{cycleTrialResult}</div>
              )}
            </div>
          </div>
        </div>
      </IpcModal>

      <IpcModal
        title="带入三年规划"
        open={bringOpen}
        onCancel={() => setBringOpen(false)}
        onOk={() => void doBring()}
        okText="带入"
      >
        <Select
          style={{ width: '100%' }}
          placeholder="选择编制中规划"
          value={bringPlanId}
          options={plans.map(p => ({ value: p.id, label: p.label }))}
          onChange={setBringPlanId}
          getPopupContainer={() => document.body}
        />
      </IpcModal>
    </div>
  );
};

export default OpportunityPool;
